import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import Checkout from '../components/Checkout';
import CustomerAuth from '../components/CustomerAuth';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { trackCheckout, trackPurchase } from '../utils/analytics';

const CheckoutPage = () => {
  const { items, clear, subtotal } = useCart();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);

  useEffect(() => {
    if (items.length > 0 && isAuthenticated) {
      trackCheckout({ subtotal, itemCount: items.length });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (items.length === 0) return <Navigate to="/cart" replace />;

  if (!isAuthenticated) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center bg-yellow-300 p-6">
        <div className="bg-white border-4 border-black shadow-[12px_12px_0px_black] max-w-md w-full p-10 text-center">
          <Lock size={42} className="mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-black tracking-tighter mb-2">SIGN IN TO CHECK OUT</h1>
          <p className="text-sm font-mono text-gray-600 mb-6">
            We use your account to track orders and let you redo purchases easily. Takes 10 seconds.
          </p>
          <button
            onClick={() => setAuthOpen(true)}
            className="w-full bg-yellow-400 text-black py-3 font-black uppercase border-4 border-black shadow-[4px_4px_0px_black] hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all"
          >
            Login / Sign up
          </button>
          <button
            onClick={() => navigate('/cart')}
            className="w-full mt-3 bg-white text-black py-2 font-black uppercase border-2 border-black hover:bg-gray-100 text-sm"
          >
            ← Back to cart
          </button>
        </div>
        <CustomerAuth
          isOpen={authOpen}
          onClose={() => setAuthOpen(false)}
          onAuthSuccess={() => setAuthOpen(false)}
        />
      </div>
    );
  }

  // Preserve real quantities; map to a backend-ready shape.
  const orderItems = items.map((it) => ({
    _id: it._id,
    id: it._id,
    name: it.name,
    price: it.salePrice ?? it.price,
    size: it.size,
    quantity: it.quantity || 1,
  }));

  return (
    <Checkout
      cart={orderItems}
      initialEmail={user?.email}
      initialName={user?.name}
      initialPhone={user?.phone}
      onBack={() => navigate('/cart')}
      onOrderComplete={(orderNumber) => {
        trackPurchase(orderNumber, { subtotal });
        clear();
        navigate(`/order/success/${encodeURIComponent(orderNumber)}`, { replace: true });
      }}
    />
  );
};

export default CheckoutPage;
